import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ConfigProvider, Segmented } from "antd";
import ReservationTabs from "./components/ReservationTabs";
import getReservationTabOptions, {
    DEFAULT_RESERVATION_TAB,
    RESERVATION_TAB_KEYS,
} from "./list/reservationTabConfig";
import PageContentShell from "../../components/layout/PageContentShell";
import ReservationTabContent from "./components/ReservationTabContent";

const LAST_TAB_STORAGE_KEY = "reservations:lastTab";
const isValidTab = (key) => Object.values(RESERVATION_TAB_KEYS).includes(key);

const readLastTab = () => {
    try {
        const stored = window.sessionStorage.getItem(LAST_TAB_STORAGE_KEY);
        return isValidTab(stored) ? stored : null;
    } catch {
        return null;
    }
};

const rememberTab = (key) => {
    try {
        window.sessionStorage.setItem(LAST_TAB_STORAGE_KEY, key);
    } catch {
        // Storage can be unavailable (private mode); the tab just isn't remembered.
    }
};

// The URL wins; otherwise the tab the user was last on. Screens that finish with a plain
// "/reservations" (check-in, check-out, reservation details > back) then land where they started
// instead of on the first tab.
const getActiveKeyFromURL = () => {
    const fromUrl = new URLSearchParams(window.location.search).get("tab");
    if (isValidTab(fromUrl)) return fromUrl;
    return readLastTab() || DEFAULT_RESERVATION_TAB;
};

const Reservation = () => {
    const { t } = useTranslation("reservations");
    const [activeKey, setActiveKey] = useState(getActiveKeyFromURL);
    const tabOptions = getReservationTabOptions(t);

    const updateURL = (key, { replace = false } = {}) => {
        const url = new URL(window.location);
        url.searchParams.set("tab", key);
        window.history[replace ? "replaceState" : "pushState"]({}, "", url);
    };

    const handleTabChange = (key) => {
        setActiveKey(key);
        rememberTab(key);
        updateURL(key);
    };

    // Reflect the tab in the URL even when it came from memory, so a refresh keeps it.
    useEffect(() => {
        const inUrl = new URLSearchParams(window.location.search).get("tab");
        if (inUrl !== activeKey) updateURL(activeKey, { replace: true });
        rememberTab(activeKey);
    }, [activeKey]);

    useEffect(() => {
        const handlePopState = () => {
            setActiveKey(getActiveKeyFromURL());
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    const segmentedOptions = [
        { label: t("upcoming"), value: "2" },
        { label: t("ongoing"), value: "3" },
        { label: t("past"), value: "4" },
        { label: t("rejected"), value: "5" },
        { label: t("cancelled"), value: "6" },
        { label: t("allReservations"), value: "7" },
    ];

    const visibleTabKey = Object.values(RESERVATION_TAB_KEYS).includes(activeKey)
        ? activeKey
        : DEFAULT_RESERVATION_TAB;

    return (
        <PageContentShell>
            <div className="flex min-w-0 flex-col gap-4">
                <div className="lg:hidden">
                    <ReservationTabs options={tabOptions} value={visibleTabKey} onChange={handleTabChange} />
                </div>
                <ConfigProvider
                    theme={{
                        components: {
                            Segmented: {
                                itemSelectedBg: "#5d5dff",
                                itemSelectedColor: "#ffffff",
                                trackPadding: "5px",
                            },
                        },
                    }}>
                    <div className="hidden lg:block" data-tour="reservations-tabs">
                        <Segmented
                            value={visibleTabKey}
                            onChange={handleTabChange}
                            options={segmentedOptions}
                            block
                            shape="round"
                            className="bg-white"
                        />
                    </div>
                </ConfigProvider>
                <ReservationTabContent key={visibleTabKey} tabKey={visibleTabKey} />
            </div>
        </PageContentShell>
    );
};

export default Reservation;
